import { useEffect, useState } from "react";
import { animated, config, SpringValue, useSpring } from "@react-spring/web";

import { DancerProps } from "../interfaces/PatternView";
import { DancerRole, Position } from "../interfaces/Choreography";
import Color from "color";

import "../styles/Visualization.css";
import {
  clampToGridPosition,
  getPositionAfterPatterRotation,
  getOrientationAfterRotation,
  getIntermediatePositionAfterPatterRotation,
  getYAfterRotation,
  getXAfterRotation,
  getYAfterRotationInverse,
  getXAfterRotationInverse,
  getStrokeFromColor,
  getFontColor,
  lumDiffAlt,
  customLighten,
  diffToLighten,
  neighborLighten,
} from "../services/VisServices";
import OrientationVisModal from "./OrientationVisModal";
import FootModal from "./FootModal";

function Dancer(props: DancerProps) {
  const colorLightenFactor = 0.6;
  let color: string = String(determineColor());

  const highlightFill = color;
  const dampedFill = "#d3d3d3"; //Light gray

  let radius: number = 0.028 * props.dms.boundedWidth;
  if (props.showShapes) {
    radius /= 1.6;
  }
  if (props.showTangentBodyParts) {
    radius /= 1.9;
  }

  const [showOrientationModal, setShowOrientationModal] =
    useState<boolean>(false);
  const [showFootModal, setShowFootModal] = useState<boolean>(false);

  function determineColor() {
    if (props.showOrientations && props.danceCouple.joint) {
      if (props.showGentlemanInCouple) {
        return props.gentlemanColor;
      } else {
        return props.ladyColor;
      }
    }

    if (props.danceCouple.joint) {
      return props.coupleColor;
    }

    switch (props.role) {
      case DancerRole.Gentleman:
        return props.gentlemanColor;
      case DancerRole.Lady:
        return props.ladyColor;
    }
  }

  function getOrientationBacksideFill(): string {
    const diff = lumDiffAlt(Color("#FFFFFF"), Color(color));
    return String(customLighten(Color(color), diffToLighten(diff)));
  }

  function getNeighborFill() {
    const diff = lumDiffAlt(Color("#FFFFFF"), Color(color));
    return String(neighborLighten(Color(color), diffToLighten(diff)));
  }

  function getFill(): string | SpringValue<string> {
    if (props.showShapes || props.showTangentBodyParts) {
      return getOrientationBacksideFill();
    }

    if (props.id === props.hoveredDancer) {
      return highlightFill;
    }
    const partnerId = props.id % 2 === 1 ? props.id + 1 : props.id - 1;
    if (
      !props.editingModeActive &&
      !props.showShapes &&
      !props.showTransitions &&
      props.hoveredDancer !== -1 &&
      props.hoveredDancer !== props.id
    ) {
      if (
        props.candidatesOnAscendingLine.includes(props.id) ||
        props.candidatesOnDescendingLine.includes(props.id) ||
        props.candidatesOnVerticalLine.includes(props.id) ||
        props.candidatesOnHorizontalLine.includes(props.id) ||
        (props.danceCouple.joint &&
          (props.candidatesOnAscendingLine.includes(partnerId) ||
            props.candidatesOnDescendingLine.includes(partnerId) ||
            props.candidatesOnVerticalLine.includes(partnerId) ||
            props.candidatesOnHorizontalLine.includes(partnerId)))
      ) {
        return getNeighborFill();
      } else {
        return dampedFill;
      }
    }

    if (props.editingModeActive || props.showTransitions) {
      return color;
    }
    //Animated fill before
    return color;
  }

  const animatedFill = useSpring({
    fill: color,
    delay: 1,
  });

  function hoverThisDancer(e: any) {
    if (props.mouseDownPosition.x > -1) {
      return;
    }
    selectThisDancer(e);
  }

  function dehoverThisDancer(e: any) {
    props.setHoveredDancer(-1);
  }

  function clickThisDancer(e: any) {
    //hideCallout();
    if (props.showOrientations) {
      setShowOrientationModal(true);
      props.setShowModal(true);
    }

    if (props.showTangentBodyParts) {
      setShowFootModal(true);
      props.setShowModal(true);
    }

    if (props.showTransitions) {
      props.setSelectedDancerForTransitions(props.id);
    }

    if (e.ctrlKey && props.editingModeActive) {
      if (!props.brushSelectedDancers.includes(props.id)) {
        let copyOfBrushSelectedDancers = [...props.brushSelectedDancers];
        if (copyOfBrushSelectedDancers.length === 0) {
          props.setFirstDancerInBrush(props.id);
        }
        copyOfBrushSelectedDancers.push(props.id);
        props.setBrushSelectedDancers(copyOfBrushSelectedDancers);
      } else {
        let copyOfBrushSelectedDancers = [...props.brushSelectedDancers];
        copyOfBrushSelectedDancers = copyOfBrushSelectedDancers.filter(
          (id) => id !== props.id
        );
        if (props.id === props.firstDancerInBrush) {
          if (copyOfBrushSelectedDancers.length > 0) {
            props.setFirstDancerInBrush(copyOfBrushSelectedDancers[0]);
          } else {
            props.setFirstDancerInBrush(-1);
          }
        }
        props.setBrushSelectedDancers(copyOfBrushSelectedDancers);
      }
    }
    e.stopPropagation();
  }

  function selectThisDancer(e: any): void {
    if (
      props.showOrientations ||
      props.showTangentBodyParts ||
      props.showShapes
    ) {
      return;
    }

    if (props.draggedDancer !== props.id) {
      props.setHoveredDancer(props.id);
    }
    e.stopPropagation();
  }

  useEffect(() => {
    color = String(determineColor());
  }, [props.role, props.showOrientations]);

  function getAnimationDuration() {
    if (!props.previousPattern) return 1000;

    let beatDistance: number = props.pattern.beat - props.previousPattern.beat;
    if (props.pattern.bar !== props.previousPattern.bar) {
      beatDistance =
        props.pattern.beat +
        1 +
        (7 - props.previousPattern.beat) +
        (props.pattern.bar - props.previousPattern.bar - 1) * 8;
    }

    const intermediatePatternAmount = props.pattern.intermediatePatterns.length;
    const totalDuration: number = beatDistance * 125;

    return (
      (totalDuration * (1 / props.animationSpeed)) /
      (intermediatePatternAmount + 1)
    );
  }

  const { transform } = useSpring({
    from: {
      transform: `translate(${props.xScale(
        getPositionAfterPatterRotation(props.previousPos, props.patternRotation)
          .x
      )}, ${props.yScale(
        getPositionAfterPatterRotation(props.previousPos, props.patternRotation)
          .y
      )})`,
    },
    to: async (next, cancel) => {
      for (let i = 0; i < props.pattern.intermediatePatterns.length; i++) {
        await next({
          transform: `translate(${props.xScale(
            getIntermediatePositionAfterPatterRotation(
              props.pattern.intermediatePatterns[i].positions[
                props.coupleId - 1
              ].position[(props.id + 1) % 2],
              props.patternRotation
            ).x
          )}, ${props.yScale(
            getIntermediatePositionAfterPatterRotation(
              props.pattern.intermediatePatterns[i].positions[
                props.coupleId - 1
              ].position[(props.id + 1) % 2],
              props.patternRotation
            ).y
          )})`,
        });
      }
      await next({
        transform: `translate(${props.xScale(
          getPositionAfterPatterRotation(props.pos, props.patternRotation).x
        )}, ${props.yScale(
          getPositionAfterPatterRotation(props.pos, props.patternRotation).y
        )})`,
      });
    },
    config: { duration: getAnimationDuration() },
  });

  function getTransform() {
    if (props.draggedDancer === props.id) {
      return calculateDraggedDancerPosition();
    }

    if (
      props.brushSelectedDancers.includes(props.id) &&
      props.draggedDancer > -1 &&
      props.brushSelectedDancers.includes(props.draggedDancer)
    ) {
      return calculateBrushDraggedDancerPosition(true);
    }

    if (!props.editingModeActive && props.currentlyInAnimation) {
      return transform;
    } else {
      return `translate(${props.xScale(
        getPositionAfterPatterRotation(props.pos, props.patternRotation).x
      )}, ${props.yScale(
        getPositionAfterPatterRotation(props.pos, props.patternRotation).y
      )})`;
    }
  }

  function mouseDownOnDancer(e: any) {
    if (
      props.showOrientations ||
      !props.editingModeActive ||
      props.showTransitions ||
      props.showTangentBodyParts ||
      props.isCalloutVisible ||
      e.button !== 0 ||
      e.ctrlKey
    ) {
      e.stopPropagation();
      return;
    }

    if (props.mouseDownPosition.x === -1 && props.mouseDownPosition.y === -1) {
      props.setDraggedDancer(props.id);
      props.setShowScalingComponent(false);
    }

    e.stopPropagation();
  }

  function mouseMoveOnDancer(e: any) {
    if (
      props.showOrientations ||
      props.isCalloutVisible ||
      props.showTransitions ||
      props.showTangentBodyParts ||
      e.button !== 0 ||
      e.ctrlKey
    ) {
      return;
    }
    props.setMouseMovePosition(e);
  }

  function mouseUpOnDancer(e: any) {
    if (
      !props.editingModeActive ||
      props.showOrientations ||
      props.showTangentBodyParts ||
      props.isCalloutVisible ||
      e.button !== 0
    ) {
      return;
    }

    const delta = calculateBrushDraggedDancerPosition(false) as {
      x: number;
      y: number;
    };
    props.brushSelectedDancers.forEach((dancerId) => {
      if (dancerId !== props.draggedDancer) {
        const originalX =
          props.pattern.positions[Math.floor((dancerId - 1) / 2)].position[
            (dancerId - 1) % 2
          ].x;
        const originalY =
          props.pattern.positions[Math.floor((dancerId - 1) / 2)].position[
            (dancerId - 1) % 2
          ].y;

        const rotatedX = getXAfterRotation(
          originalX,
          originalY,
          props.patternRotation
        );
        const rotatedY = getYAfterRotation(
          originalX,
          originalY,
          props.patternRotation
        );

        const newX = props.xScale.invert(props.xScale(rotatedX) + delta.x);
        const newY = props.yScale.invert(props.yScale(rotatedY) + delta.y);

        const rerotatedX = getXAfterRotationInverse(
          newX,
          newY,
          props.patternRotation
        );
        const rerotatedY = getYAfterRotationInverse(
          newX,
          newY,
          props.patternRotation
        );

        props.moveDancer(
          props.selectedPattern,
          dancerId,
          rerotatedX,
          rerotatedY
        );
      }
    });

    const newX = props.xScale.invert(props.currentMousePosition.x - 40);
    const newY = props.yScale.invert(props.currentMousePosition.y - 40);
    const rotatedX = getXAfterRotationInverse(
      newX,
      newY,
      props.patternRotation
    );
    const rotatedY = getYAfterRotationInverse(
      newX,
      newY,
      props.patternRotation
    );

    props.moveDancer(props.selectedPattern, props.id, rotatedX, rotatedY);

    if (props.brushSelectedDancers.length > 0) {
      props.setShowScalingComponent(true);
    }

    props.setMouseUpPosition(e);
    props.setDraggedDancer(-1);
    e.stopPropagation();
  }

  function calculateBrushDraggedDancerPosition(returnAsTransform: boolean) {
    let choreoPositionX: number;
    let choreoPositionY: number;

    const draggedDancer: Position =
      props.pattern.positions[Math.floor((props.draggedDancer - 1) / 2)]
        .position[(props.draggedDancer - 1) % 2];
    const draggedX = draggedDancer.x;
    const draggedY = draggedDancer.y;

    const rotatedDraggedX = getXAfterRotation(
      draggedX,
      draggedY,
      props.patternRotation
    );
    const rotatedDraggedY = getYAfterRotation(
      draggedX,
      draggedY,
      props.patternRotation
    );

    const deltaX =
      props.currentMousePosition.x - 40 - props.xScale(rotatedDraggedX);
    const deltaY =
      props.currentMousePosition.y - 40 - props.yScale(rotatedDraggedY);

    const unrotatedDeltaX =
      props.currentMousePosition.x - 40 - props.xScale(draggedX);
    const unrotatedDeltaY =
      props.currentMousePosition.y - 40 - props.yScale(draggedY);

    const rotatedX = getXAfterRotation(
      props.pos.x,
      props.pos.y,
      props.patternRotation
    );
    const rotatedY = getYAfterRotation(
      props.pos.x,
      props.pos.y,
      props.patternRotation
    );

    if (props.gridDragEnabled) {
      choreoPositionX = props.xScale(
        clampToGridPosition(
          props.xScale.invert(props.xScale(rotatedX) + deltaX),
          -8,
          8
        )
      );
      choreoPositionY = props.yScale(
        clampToGridPosition(
          props.yScale.invert(props.yScale(rotatedY) + deltaY),
          -8,
          8
        )
      );
    } else {
      choreoPositionX = props.xScale(rotatedX) + deltaX;
      choreoPositionY = props.yScale(rotatedY) + deltaY;
    }

    if (returnAsTransform) {
      return `translate(${choreoPositionX}, ${choreoPositionY})`;
    } else {
      return {
        x: deltaX,
        y: deltaY,
      };
    }
  }

  function calculateDraggedDancerPosition() {
    let choreoPositionX: number;
    let choreoPositionY: number;

    let discreteX;
    let discreteY;

    if (props.gridDragEnabled) {
      discreteX = clampToGridPosition(
        props.xScale.invert(props.currentMousePosition.x - 40),
        -8,
        8
      );
      choreoPositionX = props.xScale(discreteX);
      discreteY = -clampToGridPosition(
        props.xScale.invert(props.currentMousePosition.y - 40),
        -8,
        8
      );
      choreoPositionY = props.yScale(discreteY);
    } else {
      choreoPositionX = props.currentMousePosition.x - 40;
      choreoPositionY = props.currentMousePosition.y - 40;
    }

    return `translate(${choreoPositionX}, ${choreoPositionY})`;
  }

  function isSelected(): boolean {
    return props.brushSelectedDancers.includes(props.id);
  }

  /**
   * Dancers are scaled down when the shapes mode is active and the radius is
   * linearly dependent on the boundedWidth of the window.
   *
   * @returns UI code for dancers
   */
  function getDancerIcon() {
    const coupleStroke = props.colorCodedCouples
      ? getStrokeFromColor(props.coupleColors[props.coupleId].fillColor, 0.2)
      : getFill();

    if (!props.showOrientations) {
      return (
        <animated.circle
          fill={getFill()}
          r={radius}
          stroke={
            props.firstDancerInBrush === props.id
              ? "green"
              : isSelected()
              ? "black"
              : coupleStroke
          }
          strokeWidth={isSelected() ? 3 : 1}
        />
      );
    }

    let orientationToUse = props.pos.bodyOrientation;
    if (props.showOrientations && props.danceCouple.joint) {
      if (props.showGentlemanInCouple) {
        orientationToUse = props.danceCouple.position[0].bodyOrientation;
      } else {
        orientationToUse = props.danceCouple.position[1].bodyOrientation;
      }
    }

    const bodyOrientationAfterRotation = getOrientationAfterRotation(
      orientationToUse,
      props.patternRotation
    );

    //First the dancers darker front and then the lighter back
    return (
      <>
        <animated.path
          fill={getFill()}
          stroke={
            props.firstDancerInBrush === props.id
              ? "blue"
              : getStrokeFromColor(color, 0.2)
          }
          strokeWidth={isSelected() ? 3 : 1}
          d={
            "M " +
            -radius +
            " 0 A " +
            radius +
            " " +
            radius +
            " 0 0 1 " +
            radius +
            " 0"
          }
          transform={`rotate(${bodyOrientationAfterRotation})`}
        />
        <animated.path
          fill={getOrientationBacksideFill()}
          stroke={
            props.firstDancerInBrush === props.id
              ? "blue"
              : getStrokeFromColor(color, 0.2)
          }
          strokeWidth={isSelected() ? 3 : 1}
          d={
            "M " +
            -radius +
            " 0 A " +
            radius +
            " " +
            radius +
            " 0 0 1 " +
            radius +
            " 0"
          }
          transform={`rotate(${bodyOrientationAfterRotation + 180})`}
        />
      </>
    );
  }

  function getHeadOrientationMarker() {
    const radius: number = 0.028 * props.dms.boundedWidth;

    let orientationToUse = props.pos.headOrientation;
    if (props.showOrientations && props.danceCouple.joint) {
      if (props.showGentlemanInCouple) {
        orientationToUse = props.danceCouple.position[0].headOrientation;
      } else {
        orientationToUse = props.danceCouple.position[1].headOrientation;
      }
    }

    const headOrientationAfterRotation = getOrientationAfterRotation(
      orientationToUse,
      props.patternRotation
    );

    return (
      <animated.line
        x1={0}
        y1={0}
        x2={
          1.7 *
          radius *
          Math.sin(headOrientationAfterRotation * (Math.PI / 180))
        }
        y2={
          -1.7 *
          radius *
          Math.cos(headOrientationAfterRotation * (Math.PI / 180))
        }
        stroke="black"
        strokeWidth="3"
      />
    );
  }

  function getDancerLabel() {
    const roleIcon = props.danceCouple.joint
      ? "C"
      : props.role === DancerRole.Gentleman
      ? "G"
      : "L";

    if (props.colorCodedCouples) {
      return (
        <animated.text
          key={props.id}
          textAnchor="middle"
          alignmentBaseline="middle"
          fill={getFontColor(String(getFill()))}
          className="unselectable-text"
          style={{
            fontSize: radius,
            transform: "translateY(" + props.dms.boundedWidth / 200 + "px)",
          }}
        >
          {roleIcon}
        </animated.text>
      );
    }

    return (
      <animated.text
        key={props.id}
        textAnchor="middle"
        alignmentBaseline="middle"
        fill="white"
        className="unselectable-text"
        style={{
          fontSize: radius,
          transform: "translateY(" + props.dms.boundedWidth / 200 + "px)",
        }}
      >
        {props.coupleId}
      </animated.text>
    );
  }

  /**
   * Dancers icons are hidden under following conditions:
   * - Dancer icon is female and dancer is joined with partner
   * - The transition mode is active and the pair/dancer is not part of the
   *   selected transition
   * @returns Wheter the dancer icon should be visible or not.
   */
  function getDancerVisibility() {
    if (!props.showTransitions) {
      if (props.danceCouple.joint && props.id % 2 === 0) {
        return "hidden";
      } else {
        return "visible";
      }
    } else {
      if (props.selectedDancerForTransitions === -1) return "visible";
      if (props.selectedDancerForTransitions > -1) {
        if (props.pattern.id === 0) return "visible";

        if (props.selectedDancerForTransitions === props.id) {
          return "visible";
        }
        if (
          props.danceCouple.joint ||
          props.previousPattern.positions[props.coupleId - 1].joint
        ) {
          if (
            props.id % 2 === 0 &&
            props.selectedDancerForTransitions === props.id - 1
          ) {
            return "visible";
          }
          if (
            props.id % 2 === 1 &&
            props.selectedDancerForTransitions === props.id + 1
          ) {
            return "visible";
          }
        }
      }
    }

    return "hidden";
  }

  return (
    <>
      <animated.g
        key={props.id}
        id={"dancer" + props.id}
        transform={getTransform() as string}
        style={{
          cursor: "pointer",
          zIndex: props.draggedDancer === props.id ? "-1" : "0",
        }}
        onMouseOver={hoverThisDancer}
        onClick={clickThisDancer}
        onMouseLeave={dehoverThisDancer}
        onMouseDown={mouseDownOnDancer}
        onMouseUp={mouseUpOnDancer}
        onMouseMove={mouseMoveOnDancer}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          props.showCallout(
            props.xScale(
              getXAfterRotation(props.pos.x, props.pos.y, props.patternRotation)
            ) +
              0.028 * props.dms.boundedWidth,
            props.yScale(
              getYAfterRotation(props.pos.x, props.pos.y, props.patternRotation)
            ) -
              0.028 * props.dms.boundedWidth -
              5,
            props.danceCouple.joint,
            props.id
          );
        }}
        visibility={getDancerVisibility()}
      >
        {props.showOrientations && getHeadOrientationMarker()}
        {getDancerIcon()}
        {getDancerLabel()}
      </animated.g>

      <OrientationVisModal
        showModal={showOrientationModal}
        setShowModal={setShowOrientationModal}
        setShowModalForPatternView={props.setShowModal}
        selectedPattern={props.selectedPattern}
        pattern={props.pattern}
        dancerId={
          props.danceCouple.joint
            ? props.showGentlemanInCouple
              ? 2 * props.danceCouple.id - 1
              : 2 * props.danceCouple.id
            : props.id
        }
        frontsideColor={color}
        backsideColor={getOrientationBacksideFill()}
        rotateDancerBody={props.rotateDancerBody}
        rotateDancerHead={props.rotateDancerHead}
        editingModeActive={props.editingModeActive}
        brushSelectedDancers={props.brushSelectedDancers}
        joint={props.danceCouple.joint}
        pos={
          props.danceCouple.joint
            ? props.showGentlemanInCouple
              ? props.danceCouple.position[0]
              : props.danceCouple.position[1]
            : props.pos
        }
        numCouples={props.pattern.positions.length}
        showGentlemanInCouple={props.showGentlemanInCouple}
      />

      <FootModal
        showModal={showFootModal}
        setShowModal={setShowFootModal}
        setShowModalForPatternView={props.setShowModal}
        selectedPattern={props.selectedPattern}
        pattern={props.pattern}
        dancerId={props.id}
        editingModeActive={props.editingModeActive}
        brushSelectedDancers={props.brushSelectedDancers}
        joint={props.danceCouple.joint}
        pos={
          props.danceCouple.joint
            ? props.showGentlemanInCouple
              ? props.danceCouple.position[0]
              : props.danceCouple.position[1]
            : props.pos
        }
        numCouples={props.pattern.positions.length}
        showGentlemanInCouple={props.showGentlemanInCouple}
        gentlemanColor={props.gentlemanColor!.toString()}
        ladyColor={props.ladyColor!.toString()}
        coupleColor={props.coupleColor!.toString()}
        changeTangentBodyPart={props.changeTangentBodyPart}
      />
    </>
  );
}

export default Dancer;
